import React, { useContext, useMemo, useRef } from 'react'
import { Formik } from 'formik'
import moment from 'moment'
import { Button, Form, Grid, GridCell, Label, NotificationManagerContext,
   Spacer, View } from 'oio-react'
import { useParams } from 'react-router-dom'
import { Date, Input, Textarea, Time } from 'src/components/ConnectedForm'
import HtmlEditor from 'src/components/utils/HtmlEditor'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import { useInitiative, useOrganization, useUpdateInitiative } from 'src/graphql/hooks'
import style from 'src/style'

const ScheduleSessionEditGeneral = () => {
   const descriptionInputRef = useRef(null)
   const { showNotification } = useContext(NotificationManagerContext)
   const { sessionSlug } = useParams()

   const { organization } = useOrganization()
   const { initiative: event } = useContext(InitiativeHierarchyContext)
   const { initiative: session, networkStatus } = useInitiative({ slug: sessionSlug })
   const { updateInitiative } = useUpdateInitiative()

   // We use `parseZone` to ensure tz offset is preserved and the
   // time isn't converted to local time!!!! LOL!!
   const eventStartDateTimeMoment = useMemo(() => (
      event?.startDateTimeISO8601
         ? moment.parseZone(event.startDateTimeISO8601, moment.ISO_8601).startOf('day')
         : undefined
   ), [event?.startDateTimeISO8601])

   const eventEndDateTimeMoment = useMemo(() => (
      event?.endDateTimeISO8601
         ? moment.parseZone(event.endDateTimeISO8601, moment.ISO_8601).endOf('day')
         : undefined
   ), [event?.endDateTimeISO8601])

   const sessionStartDateTimeMoment = useMemo(() => (
      session?.startDateTimeISO8601
         ? moment.parseZone(session.startDateTimeISO8601, moment.ISO_8601)
         : undefined
   ), [session?.startDateTimeISO8601])

   const sessionEndDateTimeMoment = useMemo(() => (
      session?.endDateTimeISO8601
         ? moment.parseZone(session.endDateTimeISO8601, moment.ISO_8601)
         : undefined
   ), [session?.endDateTimeISO8601])

   // We don't want to display our "loading" state if the initiative
   // list is being refetched
   const initialLoading = networkStatus <= 2
   if (initialLoading) {
      return null
   }

   // The following will be changed when initiatives use page editor for real
   const sessionDescription = session?.body?.elements?.[0]?.body || ''
   const stylesheet = style.stylesheet({
      ...organization.typeStyles,
      zoom: 0.8
   })

   const handleUpdateSession = async (values, actions) => {
      const { date, startTime, endTime } = values

      try {
         await updateInitiative({ id: session.id }, {
            ...values,
            body: [{
               operation: 'updateRichText',
               updateRichText: {
                  id: session.body.elements[0].id,
                  body: descriptionInputRef.current.editor.html.get(true)
               }
            }],
            date: undefined,
            startDate: date ? date.format('YYYY-MM-DD') : null,
            startTime: startTime ? startTime.format('HH:mm:ss') : null,
            endDate: date ? date.format('YYYY-MM-DD') : null,
            endTime: endTime ? endTime.format('HH:mm:ss') : null,
            privacy: 'inherit'
         })

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      } finally {
         actions.setSubmitting(false)
      }
   }

   const eventDateTimeUtcOffset = eventStartDateTimeMoment.utcOffset()
   const isDateOutsideEventRange = (date) => {
      // Convert the date being tested to the event's timezone offset
      // If we didn't do this, it would work only some of the time.
      date.utcOffset(eventDateTimeUtcOffset, true)
      return date.isBefore(eventStartDateTimeMoment) || date.isAfter(eventEndDateTimeMoment)
   }

   return (
      <Formik
         initialValues={{
            name: session.name,
            subtitle: session.subtitle,
            location: {
               name: session.location?.name
            },
            date: sessionStartDateTimeMoment,
            startTime: sessionStartDateTimeMoment,
            endTime: sessionEndDateTimeMoment
         }}
         onSubmit={handleUpdateSession}
         render={({ handleSubmit, isSubmitting }) => (
            <Form
               elementAppearance="plain"
               elementBackgroundColor="#eee"
               elementFocusBackgroundColor="#f3f3f3"
               labelTextColor="#666"
               labelTextSize="1"
               labelTextTransform="none"
               labelTextWeight="medium"
               onSubmit={handleSubmit}>
               <View
                  position="absolute"
                  left="0px"
                  right="0px"
                  bottom="60px"
                  top="0px"
                  padding="18px"
                  scroll="on">
                  <Grid spacing="6px">
                     <GridCell colspan="12">
                        <Input
                           name="name"
                           label="Schedule Item Name"
                           size="lg"
                        />
                     </GridCell>
                     <GridCell colspan="12">
                        <Textarea
                           rows="2"
                           label="Subtitle"
                           name="subtitle"
                        />
                     </GridCell>
                     <GridCell colspan="12">
                        <Input
                           name="location.name"
                           label="Location (optional)"
                           placeholder="e.g. Grand Ballroom"
                        />
                     </GridCell>
                     <GridCell colspan="4">
                        <Label>Date</Label>
                        <Date
                           isOutsideRange={isDateOutsideEventRange}
                           name="date"
                        />
                     </GridCell>
                     <GridCell colspan="4">
                        <Label>
                           Start Time
                           {event.timezoneAbbr ? ` (${event.timezoneAbbr})` : ''}
                        </Label>
                        <Time name="startTime" />
                     </GridCell>
                     <GridCell colspan="4">
                        <Label>
                           End Time
                           {event.timezoneAbbr ? ` (${event.timezoneAbbr})` : ''}
                        </Label>
                        <Time name="endTime" />
                     </GridCell>
                     <GridCell colspan="12">
                        <Spacer size="1" />
                        <Label>Description</Label>
                        <HtmlEditor
                           ref={descriptionInputRef}
                           config={{ heightMin: 240 }}
                           initialValue={sessionDescription}
                           stylesheet={stylesheet}
                        />
                     </GridCell>
                  </Grid>
               </View>
               <View
                  position="absolute"
                  left="0px"
                  right="0px"
                  bottom="0px"
                  padding="0px 18px"
                  height="60px"
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  borderTop="1px solid #eee">
                  <Button
                     width="150px"
                     type="submit"
                     name="Save Changes"
                     color="#f7b77c"
                     textColor="#111"
                     mode={isSubmitting ? 'loading' : 'normal'}
                  />
               </View>
            </Form>
         )}
      />
   )
}

export default ScheduleSessionEditGeneral
